import React, { useState, useEffect } from "react";
import {
  useParams,
  useNavigate,
  Link,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import { Button, Card, Badge, DataTable } from "../components/ui";

const STATO_OPTIONS = {
  100000000: { label: "Attiva", variant: "success" },
  100000001: { label: "In ristrutturazione", variant: "warning" },
  100000002: { label: "Chiusa", variant: "danger" },
  100000003: { label: "In apertura", variant: "info" },
};

const TIPO_AREA_LABELS = {
  100000000: "Ala",
  100000001: "Piano",
  100000002: "Nucleo",
  100000003: "Stanza",
};

const TIPO_AREA_COLORS = {
  100000000: "bg-purple-100 text-purple-800",
  100000001: "bg-blue-100 text-blue-800",
  100000002: "bg-green-100 text-green-800",
  100000003: "bg-orange-100 text-orange-800",
};

const TIPO_AREA_ICONS = {
  100000000: "🏛️",
  100000001: "🏢",
  100000002: "🔵",
  100000003: "🚪",
};

const REGIONI = {
  100000000: "Lombardia",
  100000001: "Piemonte",
  100000002: "Veneto",
  100000003: "Emilia-Romagna",
  100000004: "Toscana",
  100000005: "Lazio",
  100000006: "Campania",
  100000007: "Sicilia",
  100000008: "Puglia",
  100000009: "Liguria",
  100000010: "Friuli-Venezia Giulia",
  100000011: "Trentino-Alto Adige",
  100000012: "Marche",
  100000013: "Abruzzo",
  100000014: "Sardegna",
  100000015: "Calabria",
  100000016: "Umbria",
  100000017: "Basilicata",
  100000018: "Molise",
  100000019: "Valle d'Aosta",
};

function DettaglioRsaPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Leggi tab dall'URL (es. /rsa/123?tab=aree)
  const tabParam = searchParams.get("tab");

  const [rsa, setRsa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scadenze, setScadenze] = useState([]);
  const [loadingScadenze, setLoadingScadenze] = useState(false);
  const [fornitori, setFornitori] = useState([]);
  const [loadingFornitori, setLoadingFornitori] = useState(false);
  const [contratti, setContratti] = useState([]);
  const [loadingContratti, setLoadingContratti] = useState(false);
  const [utentiAssociati, setUtentiAssociati] = useState([]);
  const [loadingUtenti, setLoadingUtenti] = useState(false);
  const [immobili, setImmobili] = useState([]);
  const [loadingImmobili, setLoadingImmobili] = useState(false);
  const [aree, setAree] = useState([]);
  const [loadingAree, setLoadingAree] = useState(false);
  const [apparecchiature, setApparecchiature] = useState([]);
  const [loadingApparecchiature, setLoadingApparecchiature] = useState(false);
  const [tipologieIntervento, setTipologieIntervento] = useState([]);
  const [loadingTipologie, setLoadingTipologie] = useState(false);
  const [activeTab, setActiveTab] = useState(tabParam || "info");

  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const canEdit = isAdmin || isRspp;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const rsaData = await apiRequest(`/rsa/${id}`);
        setRsa(rsaData);

        // Carica scadenze, fornitori, contratti, utenti, immobili, aree e apparecchiature in parallelo
        setLoadingScadenze(true);
        setLoadingFornitori(true);
        setLoadingContratti(true);
        setLoadingUtenti(true);
        setLoadingImmobili(true);
        setLoadingAree(true);
        setLoadingApparecchiature(true);
        setLoadingTipologie(true);

        const [
          scadenzeData,
          fornitoriData,
          contrattiData,
          utentiData,
          immobiliData,
          areeData,
          apparecchiatureData,
          tipologieData,
        ] = await Promise.all([
          apiRequest(`/rsa/${id}/scadenze`).catch(() => ({ value: [] })),
          apiRequest(`/rsa/${id}/fornitori`).catch(() => ({ value: [] })),
          apiRequest(`/rsa/${id}/contratti`).catch(() => ({ value: [] })),
          apiRequest(`/rsa/${id}/utenti`).catch(() => ({ value: [] })),
          apiRequest(`/rsa/${id}/immobili`).catch(() => ({ value: [] })),
          apiRequest(`/rsa/${id}/aree`).catch(() => ({ value: [] })),
          apiRequest(`/rsa/${id}/apparecchiature`).catch(() => ({ value: [] })),
          apiRequest(`/rsa/${id}/tipologie-intervento`).catch(() => ({
            value: [],
          })),
        ]);

        setScadenze(scadenzeData.value || []);
        setFornitori(fornitoriData.value || []);
        setContratti(contrattiData.value || []);
        setUtentiAssociati(utentiData.value || []);
        setImmobili(immobiliData.value || []);
        setAree(areeData.value || []);
        setApparecchiature(apparecchiatureData.value || []);
        setTipologieIntervento(tipologieData.value || []);
      } catch (error) {
        console.error("Error fetching RSA:", error);
        showToast("Errore nel caricamento della RSA", "error");
        navigate("/rsa");
      } finally {
        setLoading(false);
        setLoadingScadenze(false);
        setLoadingFornitori(false);
        setLoadingContratti(false);
        setLoadingUtenti(false);
        setLoadingImmobili(false);
        setLoadingAree(false);
        setLoadingApparecchiature(false);
        setLoadingTipologie(false);
      }
    };

    fetchData();
  }, [id, apiRequest, navigate, showToast]);

  const MACROCATEGORIE = {
    100000000: "Impianti elettrici",
    100000001: "Impianti idraulici",
    100000002: "Impianti termici",
    100000003: "Ascensori e montacarichi",
    100000004: "Antincendio",
    100000005: "Sicurezza",
    100000006: "Edilizia",
    100000007: "Verde e giardini",
    100000008: "Pulizie",
    100000009: "Rifiuti",
    100000010: "IT e telecomunicazioni",
    100000011: "Elettromedicali",
    100000012: "Altro",
  };

  const STATI_FORNITORE = {
    100000000: { label: "Attivo", variant: "success" },
    100000001: { label: "Inattivo", variant: "default" },
    100000002: { label: "Sospeso", variant: "warning" },
  };

  const STATI_CONTRATTO = {
    100000000: { label: "Attivo", variant: "success" },
    100000001: { label: "In scadenza", variant: "warning" },
    100000002: { label: "Scaduto", variant: "danger" },
    100000003: { label: "Annullato", variant: "default" },
  };

  const FREQUENZA_UNITA_LABELS = {
    100000000: "giorni",
    100000001: "settimane",
    100000002: "mesi",
    100000003: "anni",
  };

  const tipologieInterventoColumns = [
    {
      key: "nome",
      label: "Tipologia",
      render: (row) => (
        <span className="font-medium text-gray-900">{row.nome}</span>
      ),
    },
    {
      key: "descrizione",
      label: "Descrizione",
      render: (row) =>
        row.descrizione ? (
          <span className="text-sm text-gray-600">{row.descrizione}</span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        ),
    },
    {
      key: "fornitoreNome",
      label: "Fornitore",
      render: (row) => (
        <Link
          to={`/fornitori/${row.fornitoreId}`}
          className="text-primary-600 hover:underline font-medium"
          onClick={(e) => e.stopPropagation()}
        >
          {row.fornitoreNome}
        </Link>
      ),
    },
    {
      key: "frequenza",
      label: "Frequenza",
      render: (row) => {
        if (!row.frequenzaValore)
          return <span className="text-gray-400">-</span>;
        const unita = FREQUENZA_UNITA_LABELS[row.frequenzaUnita] || "";
        return (
          <span className="text-sm">
            ogni {row.frequenzaValore} {unita}
          </span>
        );
      },
    },
  ];

  const fornitoriColumns = [
    {
      key: "nome",
      label: "Fornitore",
      render: (row) => (
        <Link
          to={`/fornitori/${row.id}`}
          className="text-primary-600 hover:underline font-medium"
        >
          {row.nome}
        </Link>
      ),
    },
    {
      key: "macrocategoria",
      label: "Categoria",
      render: (row) => MACROCATEGORIE[row.macrocategoria] || "-",
    },
    {
      key: "email",
      label: "Email",
      render: (row) =>
        row.email ? (
          <a
            href={`mailto:${row.email}`}
            className="text-primary-600 hover:underline"
          >
            {row.email}
          </a>
        ) : (
          "-"
        ),
    },
    {
      key: "telefono",
      label: "Telefono",
      render: (row) =>
        row.telefono ? (
          <a
            href={`tel:${row.telefono}`}
            className="text-primary-600 hover:underline"
          >
            {row.telefono}
          </a>
        ) : (
          "-"
        ),
    },
    {
      key: "stato",
      label: "Stato",
      render: (row) => {
        const s = STATI_FORNITORE[row.stato] || {
          label: "N/D",
          variant: "default",
        };
        return <Badge variant={s.variant}>{s.label}</Badge>;
      },
    },
  ];

  const contrattiColumns = [
    {
      key: "nome",
      label: "Contratto",
      render: (row) => (
        <Link
          to={`/contratti/${row.id}`}
          className="text-primary-600 hover:underline font-medium"
        >
          {row.nome}
        </Link>
      ),
    },
    {
      key: "fornitore",
      label: "Fornitore",
      render: (row) =>
        row.fornitore ? (
          <Link
            to={`/fornitori/${row.fornitore.id}`}
            className="text-primary-600 hover:underline"
          >
            {row.fornitore.nome}
          </Link>
        ) : (
          "-"
        ),
    },
    {
      key: "tipologiaFornitura",
      label: "Tipo Fornitura",
      render: (row) => row.tipologiaFornitura || "-",
    },
    {
      key: "dataFine",
      label: "Scadenza",
      render: (row) =>
        row.dataFine ? new Date(row.dataFine).toLocaleDateString("it-IT") : "-",
    },
    {
      key: "importoAnnuo",
      label: "Importo Annuo",
      render: (row) =>
        row.importoAnnuo
          ? `€ ${row.importoAnnuo.toLocaleString("it-IT")}`
          : "-",
    },
    {
      key: "stato",
      label: "Stato",
      render: (row) => {
        const s = STATI_CONTRATTO[row.stato] || {
          label: "N/D",
          variant: "default",
        };
        return <Badge variant={s.variant}>{s.label}</Badge>;
      },
    },
  ];

  const scadenzeColumns = [
    {
      key: "nome",
      label: "Scadenza",
      render: (row) => (
        <Link
          to={`/scadenze/${row.id}`}
          className="text-primary-600 hover:underline font-medium"
        >
          {row.nome}
        </Link>
      ),
    },
    {
      key: "dataScadenza",
      label: "Data",
      render: (row) =>
        row.dataScadenza
          ? new Date(row.dataScadenza).toLocaleDateString("it-IT")
          : "-",
    },
    {
      key: "tipologiaIntervento",
      label: "Tipologia",
      render: (row) => row.tipologiaIntervento || "-",
    },
    {
      key: "fornitore",
      label: "Fornitore",
      render: (row) => row.fornitore || "-",
    },
    {
      key: "stato",
      label: "Stato",
      render: (row) => {
        const stati = {
          100000000: { label: "Programmata", variant: "info" },
          100000001: { label: "In corso", variant: "warning" },
          100000002: { label: "Completata", variant: "success" },
          100000003: { label: "Scaduta", variant: "danger" },
        };
        const s = stati[row.stato] || { label: "N/D", variant: "default" };
        return <Badge variant={s.variant}>{s.label}</Badge>;
      },
    },
  ];

  const TIPOLOGIE_EDIFICIO = {
    100000000: "Edificio storico",
    100000001: "Edificio moderno",
    100000002: "Villa",
    100000003: "Complesso residenziale",
    100000004: "Altro",
  };

  const immobiliColumns = [
    {
      key: "nome",
      label: "Nome/Descrizione",
      render: (row) => <span className="font-medium">{row.nome || "-"}</span>,
    },
    {
      key: "datiCatastali",
      label: "Dati Catastali",
      render: (row) => (
        <div className="text-sm">
          <span className="font-medium">F. {row.foglio || "-"}</span>
          <span className="text-gray-400 mx-1">|</span>
          <span>P. {row.particella || "-"}</span>
          {row.subalterno && (
            <>
              <span className="text-gray-400 mx-1">|</span>
              <span>Sub. {row.subalterno}</span>
            </>
          )}
        </div>
      ),
    },
    {
      key: "categoria",
      label: "Categoria",
      render: (row) => row.categoriaCatastale || "-",
    },
    {
      key: "superficie",
      label: "Superficie",
      render: (row) => (
        <div className="text-sm">
          {row.superficieCalpestabile
            ? `${row.superficieCalpestabile} mq`
            : "-"}
        </div>
      ),
    },
    {
      key: "postiLetto",
      label: "Posti Letto",
      render: (row) => (
        <div className="text-sm">
          {row.postiLettoOperativi !== undefined ? (
            <>
              <span className="font-medium">{row.postiLettoOperativi}</span>
              {row.postiLettoAutorizzati && (
                <span className="text-gray-500">
                  {" "}
                  / {row.postiLettoAutorizzati} aut.
                </span>
              )}
            </>
          ) : (
            "-"
          )}
        </div>
      ),
    },
    {
      key: "rendita",
      label: "Rendita Catastale",
      render: (row) =>
        row.renditaCatastale
          ? `€ ${Number(row.renditaCatastale).toLocaleString("it-IT")}`
          : "-",
    },
  ];

  const utentiColumns = [
    {
      key: "displayName",
      label: "Nome",
      render: (row) => (
        <Link
          to={`/admin/utenti/${row.id}`}
          className="text-primary-600 hover:underline font-medium"
        >
          {row.displayName}
        </Link>
      ),
    },
    {
      key: "email",
      label: "Email",
      render: (row) => row.email || "-",
    },
    {
      key: "ruoloLabel",
      label: "Ruolo",
      render: (row) => row.ruoloLabel || "-",
    },
    {
      key: "attivo",
      label: "Stato",
      render: (row) => (
        <Badge variant={row.attivo ? "success" : "danger"}>
          {row.attivo ? "Attivo" : "Inattivo"}
        </Badge>
      ),
    },
  ];

  const areeColumns = [
    {
      key: "tipoArea",
      label: "Tipo",
      render: (row) => (
        <span
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${TIPO_AREA_COLORS[row.tipoArea]}`}
        >
          {TIPO_AREA_ICONS[row.tipoArea]} {row.tipoAreaLabel}
        </span>
      ),
    },
    {
      key: "nome",
      label: "Nome",
      render: (row) => (
        <Link
          to={`/aree/${row.id}`}
          className="text-primary-600 hover:underline font-medium"
        >
          {row.nome}
        </Link>
      ),
    },
    {
      key: "codice",
      label: "Codice",
      render: (row) =>
        row.codice ? (
          <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs">
            {row.codice}
          </span>
        ) : (
          "-"
        ),
    },
    {
      key: "parent",
      label: "Appartiene a",
      render: (row) =>
        row.parentNome ? (
          <Link
            to={`/aree/${row.parentId}`}
            className="text-gray-600 hover:text-primary-600 text-sm"
          >
            {row.parentTipoLabel}: {row.parentNome}
          </Link>
        ) : (
          <span className="text-gray-400 text-sm">- radice -</span>
        ),
    },
    {
      key: "capacita",
      label: "Capacita'",
      render: (row) => row.capacita ?? "-",
    },
    {
      key: "attivo",
      label: "Stato",
      render: (row) => (
        <Badge variant={row.attivo ? "success" : "default"}>
          {row.attivo ? "Attiva" : "Inattiva"}
        </Badge>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  if (!rsa) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">RSA non trovata</p>
        <Button onClick={() => navigate("/rsa")} className="mt-4">
          Torna alla lista
        </Button>
      </div>
    );
  }

  const statoInfo = STATO_OPTIONS[rsa.stato] || {
    label: "N/D",
    variant: "default",
  };

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
        <Link to="/rsa" className="hover:text-primary-600">
          RSA
        </Link>
        <span>/</span>
        <span className="text-gray-900">{rsa.nome}</span>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold text-gray-900">{rsa.nome}</h1>
            <Badge variant={statoInfo.variant} dot>
              {statoInfo.label}
            </Badge>
          </div>
          <p className="text-gray-600">
            {rsa.codiceRsa && `Codice: ${rsa.codiceRsa}`}
            {rsa.codiceRsa && rsa.codiceRegionale && " | "}
            {rsa.codiceRegionale && `Cod. Regionale: ${rsa.codiceRegionale}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/rsa")}>
            Indietro
          </Button>
          {canEdit && (
            <Button onClick={() => navigate(`/admin/rsa/nuovo?id=${id}`)}>
              Modifica
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-6 overflow-x-auto">
        {[
          { id: "info", label: "Informazioni", icon: "ℹ️" },
          {
            id: "immobili",
            label: `Immobili (${immobili.length})`,
            icon: "🏠",
          },
          { id: "aree", label: `Aree (${aree.length})`, icon: "🗺️" },
          {
            id: "apparecchiature",
            label: `Apparecchiature (${apparecchiature.length})`,
            icon: "⚙️",
          },
          {
            id: "tipologie",
            label: `Tipologie (${tipologieIntervento.length})`,
            icon: "🔧",
          },
          {
            id: "fornitori",
            label: `Fornitori (${fornitori.length})`,
            icon: "🏢",
          },
          {
            id: "contratti",
            label: `Contratti (${contratti.length})`,
            icon: "📄",
          },
          {
            id: "scadenze",
            label: `Scadenze (${scadenze.length})`,
            icon: "📅",
          },
          ...(canEdit
            ? [
                {
                  id: "utenti",
                  label: `Utenti (${utentiAssociati.length})`,
                  icon: "👤",
                },
              ]
            : []),
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === tab.id
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Info */}
      {activeTab === "info" && (
        <>
          {/* Info Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Indirizzo */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span>📍</span> Indirizzo
              </h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Indirizzo</dt>
                  <dd className="font-medium text-right">
                    {rsa.indirizzo || "-"}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Comune</dt>
                  <dd className="font-medium">{rsa.comune || "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Provincia</dt>
                  <dd className="font-medium">{rsa.provincia || "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">CAP</dt>
                  <dd className="font-medium">{rsa.cap || "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Regione</dt>
                  <dd className="font-medium">{REGIONI[rsa.regione] || "-"}</dd>
                </div>
              </dl>
            </Card>

            {/* Contatti */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span>📞</span> Contatti
              </h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Telefono Centralino</dt>
                  <dd className="font-medium">
                    {rsa.telefonoCentralino ? (
                      <a
                        href={`tel:${rsa.telefonoCentralino}`}
                        className="text-primary-600 hover:underline"
                      >
                        {rsa.telefonoCentralino}
                      </a>
                    ) : (
                      "-"
                    )}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Email Direzione</dt>
                  <dd className="font-medium">
                    {rsa.emailDirezione ? (
                      <a
                        href={`mailto:${rsa.emailDirezione}`}
                        className="text-primary-600 hover:underline"
                      >
                        {rsa.emailDirezione}
                      </a>
                    ) : (
                      "-"
                    )}
                  </dd>
                </div>
                {rsa.emailCondivisioneCalendario && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Email Calendario</dt>
                    <dd className="font-medium text-sm">
                      {rsa.emailCondivisioneCalendario}
                    </dd>
                  </div>
                )}
              </dl>
            </Card>

            {/* Referente / Direttore */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span>👤</span> Referente / Direttore
              </h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Nome</dt>
                  <dd className="font-medium">{rsa.nomeReferente || "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Telefono Diretto</dt>
                  <dd className="font-medium">
                    {rsa.telefonoReferente ? (
                      <a
                        href={`tel:${rsa.telefonoReferente}`}
                        className="text-primary-600 hover:underline"
                      >
                        {rsa.telefonoReferente}
                      </a>
                    ) : (
                      "-"
                    )}
                  </dd>
                </div>
              </dl>
            </Card>

            {/* Dettagli Struttura */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span>🏥</span> Dettagli Struttura
              </h2>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Tipologia</dt>
                  <dd className="font-medium">
                    {rsa.tipologiaStruttura === 100000000
                      ? "RSA"
                      : rsa.tipologiaStruttura === 100000001
                        ? "RSD"
                        : rsa.tipologiaStruttura === 100000002
                          ? "CDI"
                          : "-"}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Codice RSA</dt>
                  <dd className="font-medium">{rsa.codiceRsa || "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Codice Regionale</dt>
                  <dd className="font-medium">{rsa.codiceRegionale || "-"}</dd>
                </div>
              </dl>
            </Card>

            {/* Calendario */}
            {rsa.outlookCalendarId && (
              <Card>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span>📅</span> Calendario
                </h2>
                <p className="text-sm text-gray-600 mb-3">
                  Calendario Outlook configurato per questa RSA
                </p>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded block overflow-x-auto">
                  {rsa.outlookCalendarId}
                </code>
              </Card>
            )}

            {/* Alias / Nomi Alternativi */}
            {rsa.alias && (
              <Card>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span>🏷️</span> Alias / Nomi Alternativi
                </h2>
                <p className="text-sm text-gray-600 mb-3">
                  Nomi con cui i fornitori potrebbero identificare questa RSA
                  nelle email
                </p>
                <div className="flex flex-wrap gap-2">
                  {rsa.alias.split(",").map((alias, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      {alias.trim()}
                    </span>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* Note */}
          {rsa.note && (
            <Card className="mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Note</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{rsa.note}</p>
            </Card>
          )}
        </>
      )}

      {/* Tab: Immobili */}
      {activeTab === "immobili" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Immobili e Dati Catastali
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Dati catastali e tecnici degli edifici della struttura
              </p>
            </div>
            {canEdit && (
              <Button onClick={() => navigate(`/immobili/nuovo?rsaId=${id}`)}>
                + Nuovo Immobile
              </Button>
            )}
          </div>

          {immobili.length > 0 ? (
            <>
              <DataTable
                data={immobili}
                columns={immobiliColumns}
                loading={loadingImmobili}
                searchable
                searchPlaceholder="Cerca immobile..."
                emptyMessage="Nessun immobile registrato per questa RSA"
                onRowClick={(row) => navigate(`/immobili/${row.id}`)}
              />

              {/* Riepilogo dati tecnici */}
              <div className="mt-6 pt-6 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">
                    {immobili
                      .reduce(
                        (sum, i) => sum + (i.superficieCalpestabile || 0),
                        0,
                      )
                      .toLocaleString("it-IT")}
                  </p>
                  <p className="text-sm text-blue-600">mq Totali</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">
                    {immobili.reduce(
                      (sum, i) => sum + (i.postiLettoOperativi || 0),
                      0,
                    )}
                  </p>
                  <p className="text-sm text-green-600">
                    Posti Letto Operativi
                  </p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-purple-700">
                    {immobili.reduce(
                      (sum, i) => sum + (i.numeroStanze || 0),
                      0,
                    )}
                  </p>
                  <p className="text-sm text-purple-600">Stanze Totali</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-orange-700">
                    {immobili.length}
                  </p>
                  <p className="text-sm text-orange-600">Immobili/Mappali</p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏠</div>
              <p className="text-gray-500 mb-4">
                Nessun immobile registrato per questa RSA
              </p>
              {canEdit && (
                <Button onClick={() => navigate(`/immobili/nuovo?rsaId=${id}`)}>
                  Aggiungi primo immobile
                </Button>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Tab: Aree */}
      {activeTab === "aree" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Aree della Struttura
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Organizzazione spaziale: Ali, Piani, Nuclei e Stanze
              </p>
            </div>
            {canEdit && (
              <Button onClick={() => navigate(`/aree/nuovo?rsaId=${id}`)}>
                + Nuova Area
              </Button>
            )}
          </div>

          {aree.length > 0 ? (
            <>
              <DataTable
                data={aree}
                columns={areeColumns}
                loading={loadingAree}
                searchable
                searchPlaceholder="Cerca area..."
                emptyMessage="Nessuna area definita per questa RSA"
                onRowClick={(row) => navigate(`/aree/${row.id}`)}
              />

              {/* Riepilogo per tipo */}
              <div className="mt-6 pt-6 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-purple-700">
                    {aree.filter((a) => a.tipoArea === 100000000).length}
                  </p>
                  <p className="text-sm text-purple-600">Ali</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">
                    {aree.filter((a) => a.tipoArea === 100000001).length}
                  </p>
                  <p className="text-sm text-blue-600">Piani</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">
                    {aree.filter((a) => a.tipoArea === 100000002).length}
                  </p>
                  <p className="text-sm text-green-600">Nuclei</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-orange-700">
                    {aree.filter((a) => a.tipoArea === 100000003).length}
                  </p>
                  <p className="text-sm text-orange-600">Stanze</p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🗺️</div>
              <p className="text-gray-500 mb-2">
                Nessuna area definita per questa RSA
              </p>
              <p className="text-sm text-gray-400 mb-4">
                Definisci ali, piani, nuclei e stanze per organizzare la
                struttura
              </p>
              {canEdit && (
                <Button onClick={() => navigate(`/aree/nuovo?rsaId=${id}`)}>
                  Aggiungi prima area
                </Button>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Tab: Apparecchiature */}
      {activeTab === "apparecchiature" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Apparecchiature Tecnologiche
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Impianti, macchinari e attrezzature della struttura
              </p>
            </div>
            {canEdit && (
              <Button
                onClick={() => navigate(`/apparecchiature/nuovo?rsaId=${id}`)}
              >
                + Nuova Apparecchiatura
              </Button>
            )}
          </div>

          {apparecchiature.length > 0 ? (
            <>
              <DataTable
                data={apparecchiature}
                columns={[
                  {
                    key: "tipo",
                    label: "Tipo",
                    render: (row) => (
                      <span className="text-sm">
                        {row.tipoApparecchiaturaLabel}
                      </span>
                    ),
                  },
                  {
                    key: "nome",
                    label: "Nome",
                    render: (row) => (
                      <Link
                        to={`/apparecchiature/${row.id}`}
                        className="text-primary-600 hover:underline font-medium"
                      >
                        {row.nome}
                      </Link>
                    ),
                  },
                  {
                    key: "marca",
                    label: "Marca/Modello",
                    render: (row) =>
                      row.marca
                        ? `${row.marca} ${row.modello || ""}`.trim()
                        : "-",
                  },
                  {
                    key: "ubicazione",
                    label: "Ubicazione",
                    render: (row) => row.ubicazioneNome || "-",
                  },
                  {
                    key: "stato",
                    label: "Stato",
                    render: (row) => (
                      <Badge
                        variant={
                          row.stato === 100000000
                            ? "success"
                            : row.stato === 100000001
                              ? "danger"
                              : row.stato === 100000002
                                ? "warning"
                                : "default"
                        }
                      >
                        {row.statoLabel}
                      </Badge>
                    ),
                  },
                ]}
                loading={loadingApparecchiature}
                searchable
                searchPlaceholder="Cerca apparecchiatura..."
                emptyMessage="Nessuna apparecchiatura registrata"
                onRowClick={(row) => navigate(`/apparecchiature/${row.id}`)}
              />

              {/* Riepilogo per stato */}
              <div className="mt-6 pt-6 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">
                    {
                      apparecchiature.filter((a) => a.stato === 100000000)
                        .length
                    }
                  </p>
                  <p className="text-sm text-green-600">Funzionanti</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-red-700">
                    {
                      apparecchiature.filter((a) => a.stato === 100000001)
                        .length
                    }
                  </p>
                  <p className="text-sm text-red-600">Guaste</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-orange-700">
                    {
                      apparecchiature.filter((a) => a.stato === 100000002)
                        .length
                    }
                  </p>
                  <p className="text-sm text-orange-600">In Manutenzione</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-gray-700">
                    {
                      apparecchiature.filter((a) => a.stato === 100000003)
                        .length
                    }
                  </p>
                  <p className="text-sm text-gray-600">Dismesse</p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">⚙️</div>
              <p className="text-gray-500 mb-2">
                Nessuna apparecchiatura registrata per questa RSA
              </p>
              <p className="text-sm text-gray-400 mb-4">
                Registra ascensori, caldaie, impianti e altre attrezzature
              </p>
              {canEdit && (
                <Button
                  onClick={() => navigate(`/apparecchiature/nuovo?rsaId=${id}`)}
                >
                  Aggiungi prima apparecchiatura
                </Button>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Tab: Tipologie Intervento */}
      {activeTab === "tipologie" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Tipologie Intervento
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Tipologie di manutenzione coperte dai fornitori associati a
                questa RSA
              </p>
            </div>
          </div>

          {tipologieIntervento.length > 0 ? (
            <>
              <DataTable
                data={tipologieIntervento}
                columns={tipologieInterventoColumns}
                loading={loadingTipologie}
                searchable
                searchPlaceholder="Cerca tipologia o fornitore..."
                emptyMessage="Nessuna tipologia trovata"
              />
              <div className="mt-6 pt-6 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">
                    {
                      new Set(tipologieIntervento.map((t) => t.tipologiaId))
                        .size
                    }
                  </p>
                  <p className="text-sm text-blue-600">Tipologie Distinte</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">
                    {
                      new Set(tipologieIntervento.map((t) => t.fornitoreId))
                        .size
                    }
                  </p>
                  <p className="text-sm text-green-600">Fornitori</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-purple-700">
                    {tipologieIntervento.length}
                  </p>
                  <p className="text-sm text-purple-600">Coperture Totali</p>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🔧</div>
              <p className="text-gray-500 mb-2">
                Nessuna tipologia intervento associata ai fornitori di questa
                RSA
              </p>
              <p className="text-sm text-gray-400">
                Associa tipologie di intervento ai fornitori collegati nella
                sezione{" "}
                <Link
                  to="/fornitori"
                  className="text-primary-600 hover:underline"
                >
                  Fornitori
                </Link>
              </p>
            </div>
          )}
        </Card>
      )}

      {/* Tab: Fornitori */}
      {activeTab === "fornitori" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Fornitori Collegati
            </h2>
          </div>
          <DataTable
            data={fornitori}
            columns={fornitoriColumns}
            loading={loadingFornitori}
            searchable
            searchPlaceholder="Cerca fornitore..."
            emptyMessage="Nessun fornitore collegato a questa RSA"
          />
        </Card>
      )}

      {/* Tab: Contratti */}
      {activeTab === "contratti" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Contratti Attivi
            </h2>
            {canEdit && (
              <Button onClick={() => navigate(`/contratti/nuovo?rsaId=${id}`)}>
                + Nuovo Contratto
              </Button>
            )}
          </div>
          <DataTable
            data={contratti}
            columns={contrattiColumns}
            loading={loadingContratti}
            searchable
            searchPlaceholder="Cerca contratto..."
            emptyMessage="Nessun contratto per questa RSA"
          />
        </Card>
      )}

      {/* Tab: Scadenze */}
      {activeTab === "scadenze" && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Scadenze Programmate
            </h2>
            <Link
              to={`/scadenze?rsaId=${id}`}
              className="text-sm text-primary-600 hover:underline"
            >
              Vedi tutte
            </Link>
          </div>
          <DataTable
            data={scadenze}
            columns={scadenzeColumns}
            loading={loadingScadenze}
            searchable
            searchPlaceholder="Cerca scadenza..."
            emptyMessage="Nessuna scadenza programmata per questa RSA"
          />
        </Card>
      )}

      {/* Tab: Utenti */}
      {activeTab === "utenti" && canEdit && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Utenti Portale Associati
            </h2>
            <p className="text-sm text-gray-500">
              Gestisci gli utenti dal pannello{" "}
              <Link
                to="/admin/utenti"
                className="text-primary-600 hover:underline"
              >
                Utenti
              </Link>
            </p>
          </div>
          <DataTable
            data={utentiAssociati}
            columns={utentiColumns}
            loading={loadingUtenti}
            searchable
            searchPlaceholder="Cerca utente..."
            emptyMessage="Nessun utente associato a questa RSA"
          />
        </Card>
      )}
    </div>
  );
}

export default DettaglioRsaPage;
